// src/components/AudioNftPlayerPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useWallet } from "@solana/wallet-adapter-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import "./SimulacrumExplorerStyle.css";
import { FetchUserTokens } from "./FetchUserTokens";
import { db } from "../firebaseConfig";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { Connection } from "@solana/web3.js";
import { getDomainKeySync, NameRegistryState } from "@bonfida/spl-name-service";


function safeName(t) {
    return (t?.name || "Unknown").toString();
}


const FREE_STYLES = [
    { id: "og-retro", label: "OG Retro" },
    { id: "modern-dark", label: "Modern Dark" },
    { id: "modern-light", label: "Modern Light" },
    { id: "vaporwave", label: "Vaporwave" },
];

const PREMIUM_STYLES = [
    { id: "neo-brutalism", label: "Neo Brutalism" },
    { id: "win95", label: "Windows 95" },
    { id: "matrix", label: "Matrix" },
    { id: "gameboy", label: "Game Boy" },
    { id: "pokedex", label: "Pokédex" },
];

const getPublicKeyFromSolDomain = async (domain) => {
    const rpc = process.env.REACT_APP_RPC_ENDPOINT;
    if (!rpc) throw new Error("RPC endpoint is not configured.");

    const connection = new Connection(rpc);

    const cleanDomain = domain.trim().toLowerCase().endsWith(".sol")
        ? domain.trim().slice(0, -4)
        : domain.trim();

    const { pubkey } = getDomainKeySync(cleanDomain);
    const { registry } = await NameRegistryState.retrieve(connection, pubkey);
    return registry.owner.toBase58();
};

export default function AudioNFTPlayerPage() {
    const { walletAddress } = useParams();
    const wallet = useWallet();
    const queryClient = useQueryClient();

    const viewAddress = walletAddress || wallet.publicKey?.toBase58();
    const rpcEndpoint = process.env.REACT_APP_RPC_ENDPOINT;

    // Player state
    const audioRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [search, setSearch] = useState("");
    const [shuffle, setShuffle] = useState(false);
    const [repeatOne, setRepeatOne] = useState(false);
    const navigate = useNavigate();

// Wallet lookup UI
    const [walletLookup, setWalletLookup] = useState(walletAddress || "");
    const [walletLookupError, setWalletLookupError] = useState("");

// Style shop theming (same behavior as WalletExplorer)
    const styleOwnerAddress = wallet.publicKey?.toBase58() || viewAddress;
    const [styleMode, setStyleMode] = useState(
          () => localStorage.getItem("walletExplorerStyle") || "og-retro"
    );
    const [unlockedStyles, setUnlockedStyles] = useState(
        () => [...FREE_STYLES.map((s) => s.id)]
    );

    const handleStyleChange = (mode) => {
        setStyleMode(mode);
        try {
            localStorage.setItem("walletExplorerStyle", mode);
        } catch (e) {
            console.warn("Could not persist style mode:", e);
        }
    };



    const { data: tokens = [], isLoading, isFetching, refetch } = useQuery({
        queryKey: ["tokens", viewAddress],
        enabled: !!viewAddress,
        staleTime: 86400000,
        queryFn: () => FetchUserTokens({ address: viewAddress, queryClient, rpcEndpoint }),
    });

    const audioTokens = useMemo(() => {
        const list = (tokens || [])
            .filter((t) => (t?.mediaType || "").toLowerCase() === "audio")
            .filter((t) => !!t.audioUrl) // must have playable source
            .filter((t) => safeName(t).toLowerCase().includes(search.trim().toLowerCase()))
            .slice();

        // playlist by name (sorted A-Z)
        list.sort((a, b) => safeName(a).localeCompare(safeName(b)));
        return list;
    }, [tokens, search]);

    const current = audioTokens[activeIndex] || null;

    // Keep activeIndex valid when filters change
    useEffect(() => {
        if (!audioTokens.length) {
            setActiveIndex(0);
            return;
        }
        if (activeIndex > audioTokens.length - 1) setActiveIndex(0);
    }, [audioTokens, activeIndex]);

    const playIndex = (idx) => {
        if (!audioTokens.length) return;
        const next = Math.max(0, Math.min(idx, audioTokens.length - 1));
        setActiveIndex(next);
    };

    const nextTrack = () => {
        if (!audioTokens.length) return;
        if (shuffle) {
            const r = Math.floor(Math.random() * audioTokens.length);
            setActiveIndex(r);
            return;
        }
        setActiveIndex((i) => (i + 1) % audioTokens.length);
    };

    const prevTrack = () => {
        if (!audioTokens.length) return;
        setActiveIndex((i) => (i - 1 + audioTokens.length) % audioTokens.length);
    };

    const togglePlay = async () => {
        const el = audioRef.current;
        if (!el) return;
        if (el.paused) await el.play();
        else el.pause();
    };

    const onEnded = () => {
        if (repeatOne) {
            const el = audioRef.current;
            if (el) {
                el.currentTime = 0;
                el.play();
            }
            return;
        }
        nextTrack();
    };

    useEffect(() => {
        const owner = styleOwnerAddress;
        if (!owner) return;

        const userDocRef = doc(db, "user_styles", owner);
        const unsubscribe = onSnapshot(userDocRef, (snap) => {
            const data = snap.data();
            const purchased = Array.isArray(data?.styles) ? data.styles : [];
            setUnlockedStyles([...FREE_STYLES.map((s) => s.id), ...purchased]);
        });

        return () => unsubscribe();
    }, [styleOwnerAddress]);

    useEffect(() => {
        const owner = styleOwnerAddress;
        if (!owner) return;

        const userDocRef = doc(db, "user_styles", owner);

        (async () => {
            const { getDoc } = await import("firebase/firestore");
            const snap = await getDoc(userDocRef);
            if (!snap.exists()) {
                await setDoc(userDocRef, {
                    styles: [],
                    createdAt: serverTimestamp(),
                });
            }
        })();
    }, [styleOwnerAddress]);

    const handleWalletLookupSubmit = async (e) => {
        e.preventDefault();
        setWalletLookupError("");

        const raw = (walletLookup || "").trim();
        if (!raw) {
            // If user clears, go to /audio and it will show connected wallet (if any)
            navigate("/audio");
            return;
        }

        try {
            let target = raw;

            if (raw.toLowerCase().endsWith(".sol")) {
                target = await getPublicKeyFromSolDomain(raw);
            }

            navigate(`/audio/${target}`);
        } catch (err) {
            setWalletLookupError(String(err?.message || err || "Could not resolve wallet"));
        }
    };


    // When track changes, auto-play (feels like a real playlist)
    useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        if (!current?.audioUrl) return;

        // load new source then play
        el.load();
        el.play().catch(() => {
            // autoplay may be blocked; user can press play
        });
    }, [current?.audioUrl]);

    if (!viewAddress) {
        return (
            <div className={`wallet-explorer-root theme-${styleMode}`}>
                <h1>Audio NFT Player</h1>
                <p>Please connect a wallet or open /audio/&lt;walletAddress&gt;.</p>
            </div>
        );
    }

    return (
          <div className="wallet-explorer-outer-root theme-${styleMode}`}">
                <div className={`wallet-explorer-root theme-${styleMode}`}>
                <div>
                    <h1>Audio NFT Player</h1>


                </div>

                    {/* Wallet lookup (browse other wallets while connected) */}
                    <form className="wallet-lookup-bar" onSubmit={handleWalletLookupSubmit}>
                        <label>Look up wallet (.sol or address)</label>
                        <div className="wallet-lookup-row">
                            <input
                                type="text"
                                value={walletLookup}
                                onChange={(e) => setWalletLookup(e.target.value)}
                                placeholder="e.g. bonfida.sol or 9xQeWvG816bUx9EP..."
                            />
                            <button type="submit">Go</button>
                            <button
                                type="button"
                                onClick={() => {
                                    setWalletLookup("");
                                    setWalletLookupError("");
                                    navigate("/audio");
                                }}
                            >
                                Connected
                            </button>
                        </div>
                        {walletLookupError ? <div className="search-error-text">{walletLookupError}</div> : null}
                    </form>
                    <div className="audio-player-header-actions">
                        <button
                            onClick={async () => {
                                localStorage.removeItem(`tokens_${viewAddress}`);
                                queryClient.invalidateQueries(["tokens", viewAddress]);
                                await refetch();
                            }}
                            disabled={isLoading || isFetching}
                        >
                            {isLoading || isFetching ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>
                    <p className="audio-player-subtitle">
                        Playlist is built from all NFTs with <strong>mediaType=audio</strong> and a playable <strong>audioUrl</strong>.
                    </p>

                    <p className="audio-player-subtitle">
                        Viewing wallet: <strong className="mono">{viewAddress}</strong>
                    </p>





            {/* Style switcher (locks match Style Shop ownership) */}
            <div className="style-switcher">
                <span className="style-switcher-label">Style:</span>

                {[...FREE_STYLES, ...PREMIUM_STYLES].map((style) => {
                    const isUnlocked = unlockedStyles.includes(style.id);
                    const isActive = styleMode === style.id;

                    return (
                        <button
                            key={style.id}
                            type="button"
                            className={isActive ? "style-switcher-button active" : "style-switcher-button"}
                            disabled={!isUnlocked}
                            onClick={() => isUnlocked && handleStyleChange(style.id)}
                            title={isUnlocked ? style.label : "Locked – purchase in Style Shop"}
                        >
                            {style.label}
                            {!isUnlocked && " 🔒"}
                        </button>
                    );
                })}
            </div>

            {(isLoading || isFetching) && (
                <div className="loading-indicator">
                    <div className="loading-spinner" />
                    <span>Loading wallet contents…</span>
                </div>
            )}

            <div className="audio-player-layout">
                {/* LEFT: playlist */}
                <aside className="audio-player-playlist">
                    <div className="audio-player-search">
                        <label>Search by name</label>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Start typing…"
                        />
                    </div>

                    <div className="audio-player-playlist-meta">
                        <p>
                            Tracks found: <strong>{audioTokens.length}</strong>
                        </p>
                    </div>

                    <div className="audio-player-tracklist">
                        {audioTokens.map((t, idx) => {
                            const isActive = idx === activeIndex;
                            return (
                                <button
                                    key={t.mint || `${t.name}-${idx}`}
                                    className={`audio-track-row ${isActive ? "active" : ""}`}
                                    onClick={() => playIndex(idx)}
                                    type="button"
                                >
                                    <div className="audio-track-row-title">{safeName(t)}</div>
                                    <div className="audio-track-row-sub mono">
                                        {t.type} {t.collectionKey ? `• ${String(t.collectionKey).slice(0, 8)}…` : ""}
                                    </div>
                                </button>
                            );
                        })}

                        {!audioTokens.length && !isLoading && !isFetching && (
                            <p style={{ opacity: 0.85 }}>
                                No audio NFTs found for this wallet (or no audioUrl present in metadata).
                            </p>
                        )}
                    </div>
                </aside>

                {/* RIGHT: player */}
                <main className="audio-player-nowplaying">
                    {current ? (
                        <>
                            <div className="audio-nowplaying-card">
                                <div className="audio-nowplaying-media">
                                    {current.image && current.image !== "No image URL" ? (
                                        <img src={current.image} alt={safeName(current)} className="audio-nowplaying-image" />
                                    ) : (
                                        <div className="audio-nowplaying-image-placeholder">No image</div>
                                    )}
                                </div>

                                <div className="audio-nowplaying-info">
                                    <h2 className="audio-nowplaying-title">{safeName(current)}</h2>
                                    <p className="audio-nowplaying-sub">
                                        <strong>Type:</strong> {current.type} • <strong>Media:</strong> {current.mediaType}
                                    </p>

                                    <div className="audio-nowplaying-links">
                                        <a
                                            href={`https://solscan.io/token/${current.mint}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            View Mint on Solscan
                                        </a>

                                        {current.collectionKey && (
                                            <a
                                                href={`https://solscan.io/token/${current.collectionKey}`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                View Collection on Solscan
                                            </a>
                                        )}

                                        {current.uri && current.uri !== "No Metadata URI" && (
                                            <a href={current.uri} target="_blank" rel="noreferrer">
                                                Open Metadata JSON
                                            </a>
                                        )}
                                    </div>

                                    <audio
                                        ref={audioRef}
                                        className="audio-player-element"
                                        controls
                                        onEnded={onEnded}
                                        src={current.audioUrl}
                                    />
                                </div>
                            </div>

                            <div className="audio-player-controls">
                                <button onClick={prevTrack} type="button">⏮ Prev</button>
                                <button onClick={togglePlay} type="button">⏯ Play/Pause</button>
                                <button onClick={nextTrack} type="button">Next ⏭</button>

                                <button
                                    type="button"
                                    className={shuffle ? "active-toggle" : ""}
                                    onClick={() => setShuffle((v) => !v)}
                                    title="Shuffle"
                                >
                                    🔀 Shuffle
                                </button>

                                <button
                                    type="button"
                                    className={repeatOne ? "active-toggle" : ""}
                                    onClick={() => setRepeatOne((v) => !v)}
                                    title="Repeat current track"
                                >
                                    🔁 Repeat 1
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="audio-nowplaying-card">
                            <p>Select a track from the playlist.</p>
                        </div>
                    )}
                </main>
            </div>
                </div>
        </div>
    );
}
